import { useState, useRef } from 'react';
import { api } from '../utils/api';
import { formatTime } from '../utils/helpers';
import { saveWorkoutLocally, getOfflineBank } from '../utils/offlineDb';
import { useTimer } from './useTimer';
import { showToast } from '../utils/toast';

const parseSafeWorkoutData = (raw) => {
    if (!raw) return null;
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string') {
        try {
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed : null;
        } catch (e) {
            return null;
        }
    }
    return null;
};

export const useWorkout = ({ token, user, exerciseDatabase, fetchInitialData, setView, setPartyMessage, setShowConfirmModal, prs, history }) => {
    const [currentWod, setCurrentWod] = useState(null);
    const [liveExercises, setLiveExercises] = useState([]);
    const [loggedPRs, setLoggedPRs] = useState([]);

    const [soundEnabled, setSoundEnabled] = useState(true);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isRecovery, setIsRecovery] = useState(false);
    const [prModal, setPrModal] = useState({ isOpen: false, exercise: null, weight: '' });
    const [swapModal, setSwapModal] = useState({ isOpen: false, idx: null, oldName: '' });
    const [swapSearch, setSwapSearch] = useState('');

    const timer = useTimer(currentWod, soundEnabled);

    const wakeLockRef = useRef(null);
    const lastWodType = useRef('default');
    const lastAvoidRef = useRef([]);

    const requestWakeLock = async () => { try { if ('wakeLock' in navigator) { wakeLockRef.current = await navigator.wakeLock.request('screen'); } } catch (err) { } };
    const releaseWakeLock = async () => { if (wakeLockRef.current !== null) { await wakeLockRef.current.release(); wakeLockRef.current = null; } };
    const toggleFullScreen = () => { if (!document.fullscreenElement) { document.documentElement.requestFullscreen().catch((err) => console.log(err)); setIsFullscreen(true); } else { if (document.exitFullscreen) { document.exitFullscreen(); setIsFullscreen(false); } } };

    const setupTimerForWod = (wod) => {
        timer.initTimerForWod(wod);
        if (wod.timeCap && (wod.format === 'AMRAP' || wod.format === 'Cardio')) timer.setTime(wod.timeCap);
        setLoggedPRs([]);
        if (wod.isFitness && wod.exercises) setLiveExercises(JSON.parse(JSON.stringify(wod.exercises))); else setLiveExercises([]);
        requestWakeLock();
    };

    const cleanupFocusMode = () => { releaseWakeLock(); if (document.fullscreenElement) { document.exitFullscreen(); setIsFullscreen(false); } localStorage.removeItem('active_session'); localStorage.removeItem('active_workout_tracking'); localStorage.removeItem('active_workout_notes'); localStorage.removeItem('focusStartedAt'); };

    const getWod = async (workoutType, clear = false, avoidList = null) => {
        if (clear) { setCurrentWod(null); return; }
        if (typeof workoutType === 'string') { lastWodType.current = workoutType; }
        if (Array.isArray(avoidList)) { lastAvoidRef.current = avoidList; }

        if (!navigator.onLine) {
            try {
                const bank = await getOfflineBank();
                if (bank && bank.length > 0) {
                    let filteredBank = bank;
                    if (user && user.training_type === 'Fitness') filteredBank = bank.filter(w => w.isFitness);
                    else if (user && user.training_type === 'Crossfit') filteredBank = bank.filter(w => !w.isFitness);
                    if (filteredBank.length === 0) filteredBank = bank;

                    const randomWod = filteredBank[Math.floor(Math.random() * filteredBank.length)];
                    const offlineWod = { ...randomWod, exercises: randomWod.exercises || [], preWorkoutAdvice: "Je traint momenteel offline. Focus vandaag extra goed op een strakke uitvoering." };
                    offlineWod.description = "📡 OFFLINE MODUS\nDeze workout is geladen uit je offline reservebank.\n\n" + (offlineWod.description || '');

                    setCurrentWod(offlineWod); setIsRecovery(false); setupTimerForWod(offlineWod); return;
                } else { showToast("Je bent offline en hebt nog geen reserve workouts.", "info"); return; }
            } catch (error) { showToast('Fout bij openen offline bank.', 'error'); return; }
        }

        try {
            const params = new URLSearchParams();
            if (lastWodType.current === 'cardio') params.set('type', 'cardio');
            else if (lastWodType.current === 'deload') params.set('deload', 'true');
            if (lastAvoidRef.current && lastAvoidRef.current.length > 0 && lastWodType.current !== 'cardio') {
                params.set('avoid', lastAvoidRef.current.join(','));
            }
            const qs = params.toString();
            const endpoint = qs ? `/generate-wod?${qs}` : '/generate-wod';

            const data = await api.fetch(endpoint, {}, token);
            if (!data || !data.wod) {
                throw new Error('Geen geldige WOD ontvangen van server');
            }

            let onlineWod = {
                ...data.wod,
                exercises: data.wod.exercises || [],
                preWorkoutAdvice: data.preWorkoutAdvice,
                isDeload: !!data.isDeload,
                avoidedMuscles: data.avoidedMuscles || data.wod?.avoidedMuscles || []
            };

            if (onlineWod.exercises && onlineWod.exercises.length > 0) {
                onlineWod.exercises = onlineWod.exercises.map(ex => {
                    let histWeight = 0;
                    let histUnit = 'KG';
                    const safeExName = ex.name || '';
                    const isDeloadEx = onlineWod.isDeload || ex.strategy === 'deload';

                    if (history && history.length > 0) {
                        for (const wod of history) {
                            if ((wod.type || '').toLowerCase().includes('deload')) continue;

                            const wodDataArr = parseSafeWorkoutData(wod.workout_data);
                            if (wodDataArr && wodDataArr.length > 0) {
                                const exMatch = wodDataArr.find(e => e && e.exercise && e.exercise.toLowerCase() === safeExName.toLowerCase());
                                if (exMatch && Array.isArray(exMatch.sets) && exMatch.sets.length > 0) {
                                    histWeight = Math.max(...exMatch.sets.map(s => parseFloat(s.weight) || 0));
                                    if (histWeight > 0) break;
                                }
                            } else if (wod.description) {
                                const safeRegexName = safeExName.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
                                const regex = new RegExp(safeRegexName + ':\\s*((?:(?:\\(W\\)\\s*)?\\d+x[\\d.]+(?:kg|lbs)?(?:,\\s*)?)+)', 'i');
                                const match = wod.description.match(regex);
                                if (match) {
                                    const weightMatches = [...match[1].matchAll(/x([\d.]+)/g)];
                                    if (weightMatches.length > 0) {
                                        histWeight = Math.max(...weightMatches.map(m => parseFloat(m[1])));
                                    }
                                    if (match[1].toLowerCase().includes('lbs')) histUnit = 'LBS';
                                    if (histWeight > 0) break;
                                }
                            }
                        }
                    }

                    if (!histWeight && prs && prs.length > 0) {
                        const prMatch = prs.find(p => p.exercise && p.exercise.toLowerCase() === safeExName.toLowerCase());
                        if (prMatch) histWeight = prMatch.weight;
                    }

                    if (histWeight > 0) {
                        if (isDeloadEx) {
                            const step = ex.step || (safeExName.toLowerCase().includes('db') || safeExName.toLowerCase().includes('dumbbell') ? 2 : 2.5);
                            const deloadWeight = Math.max(step, Math.round((histWeight * 0.65) / step) * step);
                            return { ...ex, weight: deloadWeight, unit: histUnit };
                        }
                        return { ...ex, weight: histWeight, unit: histUnit };
                    }
                    return ex;
                });
            }

            setCurrentWod(onlineWod); setIsRecovery(data.isRecovery); setupTimerForWod(onlineWod); localStorage.setItem('focusStartedAt', Date.now());
        } catch (error) {
            console.error("Fout in getWod:", error);
            showToast('Fout bij WOD ophalen.', 'error');
        }
    };

    const executeSwap = (newExerciseName) => {
        const { idx } = swapModal;
        if (idx === null || idx === undefined) return;

        const dictEntry = exerciseDatabase.find(e => { const en = e.exercise_name || e.title || e.name || ''; return en.toLowerCase() === newExerciseName.toLowerCase(); });
        const description = dictEntry ? (dictEntry.tip || dictEntry.description || dictEntry.desc) : '';

        let histWeight = 0;
        if (history && history.length > 0) {
            for (const wod of history) {
                if ((wod.type || '').toLowerCase().includes('deload')) continue;
                const wodDataArr = parseSafeWorkoutData(wod.workout_data);
                if (wodDataArr && wodDataArr.length > 0) {
                    const exMatch = wodDataArr.find(e => e && e.exercise && e.exercise.toLowerCase() === newExerciseName.toLowerCase());
                    if (exMatch && Array.isArray(exMatch.sets) && exMatch.sets.length > 0) {
                        histWeight = Math.max(...exMatch.sets.map(s => parseFloat(s.weight) || 0));
                        if (histWeight > 0) break;
                    }
                } else if (wod.description) {
                    const safeName = newExerciseName.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
                    const regex = new RegExp(safeName + ':\\s*((?:(?:\\(W\\)\\s*)?\\d+x[\\d.]+(?:kg|lbs)?(?:,\\s*)?)+)', 'i');
                    const match = wod.description.match(regex);
                    if (match) {
                        const weightMatches = [...match[1].matchAll(/x([\\d.]+)/g)];
                        if (weightMatches.length > 0) histWeight = Math.max(...weightMatches.map(m => parseFloat(m[1])));
                        if (histWeight > 0) break;
                    }
                }
            }
        }

        if (!histWeight && prs && prs.length > 0) {
            const prMatch = prs.find(p => p.exercise && p.exercise.toLowerCase() === newExerciseName.toLowerCase());
            if (prMatch) histWeight = prMatch.weight;
        }

        const isDb = newExerciseName.toLowerCase().includes('db') || newExerciseName.toLowerCase().includes('dumbbell') || newExerciseName.toLowerCase().includes('dumbell');
        const newStep = isDb ? 2 : 2.5;
        let finalWeight = histWeight > 0 ? histWeight : '';
        if (finalWeight > 0 && currentWod?.isDeload) {
            finalWeight = Math.max(newStep, Math.round((finalWeight * 0.65) / newStep) * newStep);
        }

        setLiveExercises(prev => {
            const updated = [...prev];
            const realLiveIndex = updated.findIndex((ex, i) => (ex.originalIdx !== undefined ? ex.originalIdx : i) === idx);
            if (realLiveIndex !== -1) {
                updated[realLiveIndex] = {
                    ...updated[realLiveIndex],
                    name: newExerciseName, cleanName: newExerciseName, description: description,
                    weight: finalWeight, step: newStep, isSwapped: true,
                    sets: Array.isArray(updated[realLiveIndex].sets) ? updated[realLiveIndex].sets.map(s => ({ ...s, weight: finalWeight })) : []
                };
            }
            return updated;
        });

        if (currentWod && currentWod.exercises) {
            const updatedExercises = [...currentWod.exercises];
            const realIndex = updatedExercises.findIndex((ex, i) => (ex.originalIdx !== undefined ? ex.originalIdx : i) === idx);
            if (realIndex !== -1) {
                updatedExercises[realIndex] = {
                    ...updatedExercises[realIndex],
                    name: newExerciseName, description, weight: finalWeight, step: newStep,
                    sets: Array.isArray(updatedExercises[realIndex].sets) ? updatedExercises[realIndex].sets.map(s => ({ ...s, weight: finalWeight })) : []
                };
                setCurrentWod({ ...currentWod, exercises: updatedExercises });
            }
        }

        setSwapModal({ isOpen: false, idx: null, oldName: '' }); setSwapSearch('');
    };

    const handleLogWorkout = async (payload) => {
        try {
            const savedStart = localStorage.getItem('focusStartedAt'); const realWorldTime = savedStart ? Math.floor((Date.now() - parseInt(savedStart)) / 1000) : timer.timerSeconds;
            if (!payload.duration && realWorldTime > 0) { payload.duration = realWorldTime; }
            else if (!payload.duration && timer.time > 0 && currentWod?.format !== 'AMRAP' && currentWod?.format !== 'Cardio') { payload.duration = timer.time; }
            else if (!payload.duration) { payload.duration = 1800; }

            if (!navigator.onLine) {
                await saveWorkoutLocally(payload);
                cleanupFocusMode(); setShowConfirmModal(false);
                setPartyMessage("BAM! Workout LOKAAL opgeslagen.\n\nDeze wordt gesynchroniseerd zodra je weer online bent.");
                setView('celebration'); setCurrentWod(null); timer.setIsRunning(false);
            } else {
                const res = await api.fetch('/workouts', { method: 'POST', body: JSON.stringify(payload) }, token);
                if (currentWod?.isRunSession) {
                    try {
                        await api.fetch('/running/advance', {
                            method: 'POST',
                            body: JSON.stringify({ completedKm: currentWod.targetDistanceKm || 5 })
                        }, token);
                    } catch (runAdvErr) {}
                }
                cleanupFocusMode(); setShowConfirmModal(false);

                if (res && res.brokenPRs && res.brokenPRs.length > 0) {
                    setPartyMessage("NIEUW PR GEZET!\n\n" + res.brokenPRs.map(pr => `${pr.name}: ${pr.weight}kg`).join('\n'));
                } else {
                    const firstName = user?.name ? user.name.split(' ')[0] : 'topper';
                    const messages = ['Lekker gewerkt, beest!', 'Klasse! Op naar de volgende.', 'Zweet eruit, gains erin!', 'Trots op je, topper!', `Goed bezig, ${firstName}!`, `${firstName}, je bent machine!`, 'Boom! Weer een workout in the pocket.', `Sterk werk vandaag, ${firstName}.`, 'Niet te stoppen!', `Heerlijk getraind, ${firstName}. Rust goed uit.`, 'Elke rep telt. Netjes!', `Legendary status unlocked, ${firstName}!`];
                    setPartyMessage(messages[Math.floor(Math.random() * messages.length)]);
                }

                await fetchInitialData(token);
                setView('celebration'); setCurrentWod(null); timer.setIsRunning(false);
            }
        } catch (err) { showToast("Fout bij opslaan workout!", "error"); }
    };

    const loadBenchmark = async (wod, actualText) => {
        let isFitness = false; let parsedExercises = []; const lowerText = (actualText || '').toLowerCase();
        let detectedFormat = 'For Time'; let timeCap = 0;

        if (lowerText.includes('cardio sessie') || wod.type?.toLowerCase().includes('cardio')) {
            detectedFormat = 'Cardio'; isFitness = false;
            const match = lowerText.match(/tijd:\s*(\d+)/) || lowerText.match(/(\d+)\s*minuten/);
            timeCap = match ? parseInt(match[1], 10) * 60 : 0;
        }
        else if (lowerText.includes('amrap')) {
            detectedFormat = 'AMRAP';
            const match = lowerText.match(/amrap\s*(\d+)/) || lowerText.match(/(\d+)\s*minuten\s*amrap/);
            timeCap = match ? parseInt(match[1] || match[2], 10) * 60 : 12 * 60;
        }
        else if (lowerText.includes('emom')) { detectedFormat = 'EMOM'; }
        else if (lowerText.includes('tabata')) { detectedFormat = 'Tabata'; }
        else if (lowerText.includes('chipper') || wod.type?.toLowerCase().includes('chipper')) { detectedFormat = 'Chipper'; }
        else if (lowerText.includes('voor tijd') || lowerText.includes('for time')) { detectedFormat = 'For Time'; }
        else if (lowerText.includes('krachttraining') || wod.type?.toLowerCase().includes('kracht')) { detectedFormat = 'Krachttraining'; isFitness = true; }

        if (actualText.match(/\d+x[\d.]+/) && detectedFormat !== 'Cardio') {
            detectedFormat = 'Krachttraining'; isFitness = true;
        }

        if (isFitness) {
            const lines = actualText.split('\n');
            let lastParsedIndex = -1;

            lines.forEach(line => {
                try {
                    if (line.includes('📝 Notitie:')) {
                        if (lastParsedIndex >= 0) { parsedExercises[lastParsedIndex].note = line.split('📝 Notitie:')[1].trim(); }
                        return;
                    }

                    let name = ""; let setsCount = 4; let reps = "10"; let weight = 0;
                    const matchNew = line.match(/^-\s*(?:[A-Z]\d?\s*-\s*)?(.*?):\s*(.*)/);
                    const matchOld = line.match(/-\s*(\d+)x([\d-]+)\s+(.*?)\s*\((.*?)\)/);
                    const matchSkipped = line.match(/^-\s*(?:[A-Z]\d?\s*-\s*)?0x0\s*(.*?)\s*\(Overgeslagen\)/);

                    if (matchSkipped) {
                        name = matchSkipped[1].trim(); setsCount = 4; reps = "10"; weight = 0;
                    } else if (matchNew && !line.includes('Overgeslagen')) {
                        name = matchNew[1].trim();
                        const setsStr = matchNew[2].split(','); setsCount = setsStr.length;

                        let maxW = 0; let lastReps = "10";
                        setsStr.forEach(s => {
                            const sm = s.match(/(\d+)x([\d.]+)/);
                            if (sm) { lastReps = sm[1]; const w = parseFloat(sm[2]); if (w > maxW) maxW = w; }
                        });
                        reps = lastReps; weight = maxW;

                    } else if (matchOld) {
                        setsCount = parseInt(matchOld[1], 10); reps = matchOld[2]; name = matchOld[3].trim();
                        const weightStr = matchOld[4].toLowerCase(); if (weightStr.includes('kg')) weight = parseFloat(weightStr.replace('kg', ''));
                    } else { return; }

                    name = name.replace(/^(\d+x[\d.]+(kg|lbs)?\s*)+/i, '').trim();
                    if (!name) return;

                    let step = name.toLowerCase().includes('db') || name.toLowerCase().includes('dumbell') || name.toLowerCase().includes('dumbbell') ? 2 : 2.5;
                    const dictEntry = (exerciseDatabase || []).find(e => (e?.exercise_name || e?.name || e?.title)?.toLowerCase() === name.toLowerCase());
                    const description = dictEntry ? (dictEntry.tip || dictEntry.description || dictEntry.desc) : '';

                    let unit = 'KG'; if (line.toLowerCase().includes('lbs')) unit = 'LBS';
                    parsedExercises.push({ name, sets: setsCount, reps, weight, step, description, note: '', unit });
                    lastParsedIndex = parsedExercises.length - 1;
                } catch (e) { console.error("Parse error op history line: ", line); }
            });
        }

        if (isFitness && navigator.onLine && parsedExercises.length > 0) {
            try {
                const refreshedData = await api.fetch('/workouts/history-refresh', { method: 'POST', body: JSON.stringify({ exercises: parsedExercises }) }, token);
                if (refreshedData && refreshedData.exercises) {
                    parsedExercises = refreshedData.exercises.map(refEx => {
                        const orig = parsedExercises.find(p => p.name.toLowerCase() === refEx.name.toLowerCase());
                        return { ...refEx, weight: orig && orig.weight > 0 ? orig.weight : refEx.weight, unit: orig ? orig.unit : 'KG' };
                    });
                }
            } catch (err) { console.warn('AI Refresh failed, using standard history data.'); }
        }

        const rebuiltWod = { title: `Historie: ${wod.type}`, format: detectedFormat, type: wod.type, description: actualText, timeCap: timeCap, intensity: wod.intensity || 5, isFitness: isFitness, exercises: parsedExercises };
        setCurrentWod(rebuiltWod); setupTimerForWod(rebuiltWod); setView('dashboard'); window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return {
        currentWod, setCurrentWod, liveExercises, setLiveExercises, loggedPRs, setLoggedPRs,
        soundEnabled, setSoundEnabled, isFullscreen, setIsFullscreen, isRecovery, setIsRecovery,
        prModal, setPrModal, swapModal, setSwapModal, swapSearch, setSwapSearch,
        requestWakeLock, releaseWakeLock, toggleFullScreen, setupTimerForWod, cleanupFocusMode,
        getWod, executeSwap, handleLogWorkout, loadBenchmark,
        ...timer
    };
};
